import { convertMetersToFeet } from '@Utils/helpers/numericalHelpers';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import {
    DEFAULT_IKE_OFFICE_IMAGE_HEIGHT,
    DEFAULT_IKE_OFFICE_IMAGE_WIDTH,
} from '@Data/qualityControl/Helpers';
import {
    IProcessedQcImage,
    IQcPole,
    IQcSpanEntity,
    ISvgLabelData,
    ImageDeltas,
    QcPhotoPosition,
    SvgLabelType,
} from '@Data/qualityControl/Interfaces';
import symbology from '@Views/QualityControl/styles/symbology.scss';
import {
    LABEL_HEIGHT,
    LABEL_MIN_GAP,
    LG_LABEL_WIDTH,
    NARROW_GUTTER_WIDTH,
    SM_LABEL_WIDTH,
    TEXT_BASE_HEIGHT,
    WIDE_GUTTER_WIDTH,
    XL_LABEL_WIDTH,
} from './constants';

export const truncateLabelText = (text: string, maxLength: number) => {
    if (text.length > maxLength) {
        if (!text.includes(' > ')) {
            return text.substring(0, maxLength);
        }

        const sections = text.split(' > ');
        const truncatedSections = [];

        for (const section of sections) {
            const truncatedSection = section.replace(/\s/g, '').substring(0, 3);
            truncatedSections.push(truncatedSection);
        }

        const truncatedString = truncatedSections.join('>');

        if (truncatedString.length <= maxLength) {
            const remainingLength = maxLength - truncatedString.length;
            const firstSections = truncatedSections.slice(0, -1);
            const lastSection = sections[sections.length - 1]
                .replace(/\s/g, '')
                .substring(0, remainingLength);
            return firstSections.join('>') + '>' + lastSection;
        } else {
            return truncatedString.substring(0, maxLength);
        }
    } else {
        return text;
    }
};

export const renderBlankImage = (
    svg,
    xCoord: number,
    imgContainerWidth,
    viewerHeight,
    blankImageTextValue: string
) => {
    const blankImage = svg
        .append('rect')
        .attr('x', xCoord)
        .attr('y', 0)
        .attr('width', imgContainerWidth)
        .attr('height', viewerHeight)
        .attr('fill', '#f2f2f2');

    const blankImageText = svg
        .append('text')
        .attr('x', xCoord + imgContainerWidth / 2)
        .attr('y', viewerHeight / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .attr('fill', 'black')
        .text(blankImageTextValue);
};

export const getContainerBounds = (
    imageXCoords: number,
    imgContainerWidth: number,
    viewerHeight: number
) => {
    const x1 = imageXCoords;
    const x2 = x1 + imgContainerWidth;
    const y1 = 0;
    const y2 = viewerHeight;
    return { x1, x2, y1, y2 };
};

export const getOriginalImageBounds = (
    imageXCoords: number,
    imageXCorrection: number,
    imageWidth: number,
    viewerHeight: number
) => {
    const x1 = imageXCoords - imageXCorrection;
    const x2 = x1 + imageWidth;
    const y1 = 0;
    const y2 = viewerHeight;
    return { x1, x2, y1, y2 };
};

export const getImageDeltas = (
    viewerHeight: number,
    resolutionHeight: number,
    resolutionWidth: number,
    imageContainerWidth: number
): ImageDeltas => {
    const imageRatio = viewerHeight / resolutionHeight;
    const imageWidth = resolutionWidth * imageRatio;
    const imageXCorrection = (imageWidth - imageContainerWidth) / 2;
    return { imageRatio, imageWidth, imageXCorrection };
};

export const scaleAndSortYvalues = (
    yValuesArray: number[],
    imageRatio: number
): number[] => {
    const scaledYs = yValuesArray.map((yValue: number) => {
        return yValue * imageRatio;
    });
    return scaledYs.sort((a, b) => a - b);
};

export const findLabelYs = (yValuesArray: number[]): number[] => {
    const labelConatinerHeight = 25;
    let yMin = 0;
    for (let i = 0; i < yValuesArray.length; i++) {
        if (yValuesArray[i] < yMin) {
            yValuesArray[i] = yMin;
            yMin += labelConatinerHeight;
        } else if (
            yValuesArray[i + 1] - yValuesArray[i] <
            labelConatinerHeight
        ) {
            const mean = (yValuesArray[i] + yValuesArray[i + 1]) / 2;
            if (mean - 25 <= yMin) {
                yValuesArray[i] = yMin;
                yValuesArray[i + 1] = yMin + labelConatinerHeight;
                yMin = yValuesArray[i] + labelConatinerHeight;
            } else {
                yValuesArray[i] = mean - labelConatinerHeight;
                yValuesArray[i + 1] = mean;
                yMin = yValuesArray[i] + labelConatinerHeight;
            }
        } else {
            yMin = yValuesArray[i] + labelConatinerHeight;
        }
    }
    return yValuesArray;
};

export const getModifiedLabelParams = (
    viewerHeight: number,
    resolutionHeight: number,
    resolutionWidth: number,
    imageContainerWidth: number,
    backLabelsArray?: ISvgLabelData[],
    forwardLabelsArray?: ISvgLabelData[]
): {
    adjustedBackLabels: ISvgLabelData[];
    adjustedForwardLabels: ISvgLabelData[];
    imgDeltas: ImageDeltas;
} => {
    const imgDeltas = getImageDeltas(
        viewerHeight,
        resolutionHeight,
        resolutionWidth,
        imageContainerWidth
    );
    const backLabelsByAscendingYs = [...backLabelsArray].sort(
        (a, b) => a.y - b.y
    );
    const backLabelsRawYs = backLabelsArray.map(
        (label: ISvgLabelData) => label.y
    );
    const backLabelYsScaledandSorted = scaleAndSortYvalues(
        backLabelsRawYs,
        imgDeltas.imageRatio
    );
    const backLabelYs = findLabelYs(backLabelYsScaledandSorted);
    const adjustedBackLabels = backLabelsByAscendingYs.map(
        (label: any, index) => {
            const adjustedX =
                label.x * imgDeltas.imageRatio - imgDeltas.imageXCorrection;
            const adjustedY = label.y * imgDeltas.imageRatio;
            const adjustedLabelY = backLabelYs[index];
            return {
                ...label,
                x: adjustedX,
                y: adjustedY,
                labelY: adjustedLabelY,
            };
        }
    );

    const forwardLabelsByAscendingYs = [...forwardLabelsArray].sort(
        (a, b) => a.y - b.y
    );
    const forwardLabelsRawYs = forwardLabelsArray.map((label: any) => label.y);
    const forwardLabelYsScaledandSorted = scaleAndSortYvalues(
        forwardLabelsRawYs,
        imgDeltas.imageRatio
    );
    const forwardLabelYs = findLabelYs(forwardLabelYsScaledandSorted);
    const adjustedForwardLabels = forwardLabelsByAscendingYs.map(
        (label: any, index) => {
            const adjustedX =
                label.x * imgDeltas.imageRatio - imgDeltas.imageXCorrection;
            const adjustedY = label.y * imgDeltas.imageRatio;
            const adjustedLabelY = forwardLabelYs[index];
            return {
                ...label,
                x: adjustedX,
                y: adjustedY,
                labelY: adjustedLabelY,
            };
        }
    );

    return { adjustedBackLabels, adjustedForwardLabels, imgDeltas };
};

export const processPhotosData = (
    photoData: (IQcPole | IQcSpanEntity)[],
    viewerHeight: number,
    imgContainerWidth: number
): IProcessedQcImage[] => {
    function mapNoHeightLabels(labelsArray: ISvgLabelData[]): ISvgLabelData[] {
        return labelsArray.map((l, i) => {
            return {
                ...l,
                index: i,
            };
        });
    }

    const processedPhotosData: IProcessedQcImage[] = [];
    photoData.forEach((photoObj: IQcPole | IQcSpanEntity) => {
        const newLabelParams = getModifiedLabelParams(
            viewerHeight,
            photoObj?.truesizeImageInfo[0]?.resolution.height ??
                DEFAULT_IKE_OFFICE_IMAGE_HEIGHT,
            photoObj?.truesizeImageInfo[0]?.resolution.width ??
                DEFAULT_IKE_OFFICE_IMAGE_WIDTH,
            imgContainerWidth,
            photoObj?.backwardLabels,
            photoObj?.forwardLabels
        );
        const newPhotoParams: IProcessedQcImage = {
            ...photoObj,
            backwardLabels: newLabelParams.adjustedBackLabels,
            forwardLabels: newLabelParams.adjustedForwardLabels,
            imageDeltas: newLabelParams.imgDeltas,
        };

        if (newPhotoParams.forwardNoHeightLabels) {
            newPhotoParams.forwardNoHeightLabels = mapNoHeightLabels(
                newPhotoParams.forwardNoHeightLabels
            );
        }

        if (newPhotoParams.backwardNoHeightLabels) {
            newPhotoParams.backwardNoHeightLabels = mapNoHeightLabels(
                newPhotoParams.backwardNoHeightLabels
            );
        }

        processedPhotosData.push(newPhotoParams);
    });

    return processedPhotosData;
};

export function getImageContainerWidth(windowWidth: number): number {
    return (windowWidth - WIDE_GUTTER_WIDTH * 2 - NARROW_GUTTER_WIDTH * 2) / 5;
}

export function getImageXCoordinates(
    windowWidth: number,
    imageContainerWidth: number
): [number, number, number, number, number] {
    return [
        0,
        imageContainerWidth + NARROW_GUTTER_WIDTH,
        windowWidth / 2 - imageContainerWidth / 2,
        windowWidth - (imageContainerWidth * 2 + NARROW_GUTTER_WIDTH),
        windowWidth - imageContainerWidth,
    ];
}

export function getHorizontalOffset(imgContainerWidth: number): number {
    return imgContainerWidth / 20;
}

export const getBorderColor = (index) => {
    if (index === 0) {
        return symbology.ikeQc2Backpole;
    } else if (index === 2) {
        return symbology.ikeQc2Centerpole;
    } else if (index === 4) {
        return symbology.ikeQc2Forepole;
    } else {
        return 'transparent';
    }
};

export const getSymbology = (
    data: ISvgLabelData,
    direction: 'back' | 'fore',
    imagePosition: QcPhotoPosition
): string => {
    if (data.point_type === 'base') {
        return symbology.agl;
    }

    switch (data.type) {
        case 'agl':
            return symbology.agl;
        case 'equipment':
            return symbology.equipment;
        case 'mre':
            return symbology.makeReady;
        case 'anchor':
            return symbology.anchor;
        case 'guy':
            return symbology.anchor;
        case 'spanGuy':
            return symbology.equipment;
        case 'dropspan':
            return symbology.dropSpan;
        case 'generic':
            return '#000';
        default:
            if (
                imagePosition === QcPhotoPosition.BACKPOLE ||
                imagePosition === QcPhotoPosition.BACKSPAN
            ) {
                return symbology.backPole;
            } else if (
                imagePosition === QcPhotoPosition.FOREPOLE ||
                imagePosition === QcPhotoPosition.FORESPAN
            ) {
                return symbology.forePole;
            } else {
                return direction === 'back'
                    ? symbology.backPole
                    : symbology.forePole;
            }
    }
};

export function getNoHeightLabelYValue(
    viewerHeight: number,
    labelIndex: number,
    text = false
): number {
    let start = viewerHeight - 1 - LABEL_HEIGHT;

    if (text) {
        start = start + LABEL_HEIGHT - LABEL_MIN_GAP;
    }

    return start - (LABEL_HEIGHT + LABEL_MIN_GAP) * labelIndex;
}

/**
 * Render the labels for a Pole or Span's dataset
 */
export function appendSvgDataLabels(
    svg,
    dataset: ISvgLabelData[],
    className: string,
    onClickHandler: (
        data: ISvgLabelData,
        event,
        direction: 'forward' | 'backward',
        hasHeight: boolean
    ) => void,
    photoPosition: QcPhotoPosition,
    basePosition: number,
    hasHeight: boolean,
    direction: 'back' | 'fore',
    dimensions: {
        imgContainerWidth: number;
        viewerHeight: number;
    }
) {
    // container g elements
    const labelGroups = svg
        .selectAll(className)
        .data(dataset)
        .join('g')
        .filter((d: ISvgLabelData) => !_isNil(d.name))
        .attr('class', className)
        .style('cursor', (d: ISvgLabelData) =>
            d.dropdownData?.length > 0 ? 'pointer' : 'default'
        )
        .on('click', onClickHandler);

    function getRectX() {
        if (direction === 'back') {
            return hasHeight
                ? basePosition - LG_LABEL_WIDTH - SM_LABEL_WIDTH
                : basePosition - XL_LABEL_WIDTH - LABEL_MIN_GAP;
        } else {
            return hasHeight
                ? basePosition + dimensions.imgContainerWidth + SM_LABEL_WIDTH
                : basePosition + dimensions.imgContainerWidth + LABEL_MIN_GAP;
        }
    }

    function getRectY(d: ISvgLabelData) {
        return hasHeight
            ? d.labelY
            : getNoHeightLabelYValue(dimensions.viewerHeight, d.index);
    }

    function getTextX() {
        if (direction === 'back') {
            return hasHeight
                ? basePosition - LG_LABEL_WIDTH / 2 - SM_LABEL_WIDTH
                : basePosition - XL_LABEL_WIDTH / 2 - LABEL_MIN_GAP;
        } else {
            return hasHeight
                ? basePosition +
                      dimensions.imgContainerWidth +
                      SM_LABEL_WIDTH +
                      LG_LABEL_WIDTH / 2
                : basePosition +
                      dimensions.imgContainerWidth +
                      XL_LABEL_WIDTH / 2 +
                      LABEL_MIN_GAP;
        }
    }

    function getTextY(d: ISvgLabelData) {
        return hasHeight
            ? d.labelY + TEXT_BASE_HEIGHT
            : getNoHeightLabelYValue(dimensions.viewerHeight, d.index, true);
    }

    function getHeightRectX() {
        return direction === 'back'
            ? basePosition - SM_LABEL_WIDTH
            : basePosition + dimensions.imgContainerWidth;
    }

    function getHeightTextX() {
        return direction === 'back'
            ? basePosition - SM_LABEL_WIDTH / 2
            : basePosition + dimensions.imgContainerWidth + SM_LABEL_WIDTH / 2;
    }

    const polePositions = [
        QcPhotoPosition.BACKPOLE,
        QcPhotoPosition.CENTREPOLE,
        QcPhotoPosition.FOREPOLE,
    ];

    // if it's a pole, render labels
    if (polePositions.includes(photoPosition)) {
        // label rect elements
        labelGroups
            .append('rect')
            .attr('x', getRectX)
            .attr('y', (d: ISvgLabelData) => getRectY(d))
            .attr('width', hasHeight ? LG_LABEL_WIDTH : XL_LABEL_WIDTH)
            .attr('height', LABEL_HEIGHT)
            .attr('fill', '#f2f2f2')
            .attr('stroke', (d: ISvgLabelData) =>
                getSymbology(d, direction, photoPosition)
            )
            .attr('stroke-width', 1)
            .attr('rx', 5)
            .attr('ry', 5);

        // label text elements
        labelGroups
            .append('text')
            .attr('x', getTextX)
            .attr('y', (d: ISvgLabelData) => getTextY(d))
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .attr('fill', 'black')
            .text((d: ISvgLabelData) =>
                truncateLabelText(d.name, hasHeight ? 18 : 16)
            );
    }

    // hover text
    labelGroups
        .append('title')
        .text(
            (d: ISvgLabelData) =>
                `${d.name}${
                    hasHeight ? ` (${convertMetersToFeet(d.height)})` : ''
                }`
        );

    if (hasHeight) {
        // height rect elements
        labelGroups
            .append('rect')
            .attr('x', getHeightRectX)
            .attr('y', (d: ISvgLabelData) => d.labelY)
            .attr('width', SM_LABEL_WIDTH)
            .attr('height', LABEL_HEIGHT)
            .attr('fill', '#f2f2f2')
            .attr('stroke', (d: ISvgLabelData) =>
                getSymbology(d, direction, photoPosition)
            )
            .attr('stroke-width', 1)
            .attr('rx', 5)
            .attr('ry', 5);

        // height text elements
        labelGroups
            .append('text')
            .attr('x', getHeightTextX)
            .attr('y', (d: ISvgLabelData) => d.labelY + TEXT_BASE_HEIGHT)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .attr('fill', 'black')
            .text((d: ISvgLabelData) => convertMetersToFeet(d.height));
    }
}
